/**
 * Running moderation and account actions honestly:
 * - a step-up answer opens the step-up dialog with the reason the server's
 *   code gives, then retries once: `MODERATOR_BRAKE` (100032, the brake),
 *   `FRESH_STEP_UP_REQUIRED` (100031, a destructive action) or
 *   `STEP_UP_REQUIRED` (100001, account security);
 * - every other refusal becomes a calm toast in the user's language
 *   (hierarchy, two-step rule, new-account period with its end date);
 * - success is reported only after the server answered.
 */
import { ApiErrorCode } from '@neosocial/protocol';
import { create } from 'zustand';
import { tr } from '../i18n';
import { isApiRequestError } from '../lib/api';
import { toast } from '../ui/overlay';

/* ---------------- step-up (brake, destructive, account) ---------------- */

/** What the step-up dialog asks for, and why. */
export interface StepUpChallenge {
  /**
   * - `brake`: more than 10 bans, kicks or timeouts by one person in 10
   *   minutes; the owner and Admins have been told (`mod.brake`);
   * - `destructive`: deleting channels or roles, giving Administrator or a
   *   Manage permission (`mod.brake.destructive`);
   * - `account`: account security (sessions, two-step, transfers).
   */
  reason: 'brake' | 'destructive' | 'account';
  /** A two-step code where the account has two-step, otherwise the password. */
  level: 'password' | 'second_factor';
  /** How recent the step-up must be, in seconds. */
  max_age_s: number;
}

/** The server's step-up refusals. */
const STEP_UP_CODES: readonly number[] = [
  ApiErrorCode.STEP_UP_REQUIRED,
  ApiErrorCode.FRESH_STEP_UP_REQUIRED,
  ApiErrorCode.MODERATOR_BRAKE,
];

/** True for a refusal that a step-up and one retry can fix. */
export function needsStepUp(e: unknown): boolean {
  return isApiRequestError(e) && STEP_UP_CODES.includes(e.code);
}

export interface StepUpAsk {
  challenge: StepUpChallenge;
  resolve(ok: boolean): void;
}

export const useStepUp = create<{ ask: StepUpAsk | null }>(() => ({ ask: null }));

/** Opens the step-up dialog; resolves true once the server accepted a code or password. */
export function askStepUp(challenge: StepUpChallenge): Promise<boolean> {
  return new Promise((resolve) => {
    useStepUp.getState().ask?.resolve(false);
    useStepUp.setState({ ask: { challenge, resolve } });
  });
}

const DEFAULT_CHALLENGE: StepUpChallenge = {
  reason: 'account',
  level: 'password',
  max_age_s: 300,
};

/**
 * The challenge for a step-up refusal: the brake and destructive actions
 * need a fresh step-up (a two-step code where the account has two-step, 5
 * minutes); a plain STEP_UP_REQUIRED keeps the caller's hint.
 */
export function challengeOf(e: unknown, fallback?: Partial<StepUpChallenge>): StepUpChallenge {
  const code = isApiRequestError(e) ? e.code : 0;
  if (code === ApiErrorCode.MODERATOR_BRAKE)
    return { reason: 'brake', level: 'second_factor', max_age_s: 300 };
  if (code === ApiErrorCode.FRESH_STEP_UP_REQUIRED)
    return { reason: 'destructive', level: 'second_factor', max_age_s: 300 };
  return { ...DEFAULT_CHALLENGE, ...fallback };
}

/**
 * "Try again in about …" for a wait of `seconds` (a 429's `retry_after`):
 * hours from 90 minutes up, else minutes; "later" when the server gave none.
 */
export function waitText(seconds: number | null): string {
  const { t } = tr();
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return t('ns.err.tryLater');
  if (seconds >= 90 * 60) return t('ns.err.tryInHours', { n: Math.round(seconds / 3600) });
  return t('ns.err.tryInMinutes', { n: Math.max(1, Math.ceil(seconds / 60)) });
}

/** The message for a refused action, in the user's language. */
export function errorText(e: unknown): string {
  const { t, has, fmt } = tr();
  if (!isApiRequestError(e)) return t('ns.err.network');
  if (e.code === ApiErrorCode.ROLE_HIERARCHY) return t('mod.cantHigher');
  if (e.code === ApiErrorCode.MFA_REQUIRED_FOR_MODERATION) return t('mod.needMfa');
  // a server-creation limit: which one, and about how long to wait (QA APP-SV-02)
  const limit = e.body?.nx_guild_limit;
  if (
    e.code === ApiErrorCode.RATE_LIMITED &&
    limit &&
    (limit.per === 'day' || limit.per === 'hour')
  )
    return `${t(`ns.err.guildLimit.${limit.per}`, { n: limit.max })} ${waitText(e.retryAfter)}`;
  const key = `ns.err.${e.code}`;
  // limited, suspended or being deleted by staff: say until when (QA CO-BUG-16)
  const until = e.body?.nx_until;
  if (typeof until === 'string' && has(`${key}.until`))
    return t(`${key}.until`, { date: fmt.date(until) });
  return has(key) ? t(key) : t('ns.err.generic');
}

export function failToast(e: unknown): void {
  toast(errorText(e), { tone: 'danger', icon: 'alert' });
}

/**
 * Runs `fn`; on a step-up refusal asks once and retries. Returns the result,
 * or null when the user cancelled or the action was refused (already toasted
 * unless `quiet`).
 */
export async function runAction<T>(
  fn: () => Promise<T>,
  o: { challenge?: Partial<StepUpChallenge>; quiet?: boolean } = {},
): Promise<{ ok: true; value: T } | { ok: false; error: unknown }> {
  try {
    return { ok: true, value: await fn() };
  } catch (e) {
    if (!needsStepUp(e)) {
      if (!o.quiet) failToast(e);
      return { ok: false, error: e };
    }
    const ok = await askStepUp(challengeOf(e, o.challenge));
    if (!ok) return { ok: false, error: e };
    try {
      return { ok: true, value: await fn() };
    } catch (e2) {
      if (!o.quiet) failToast(e2);
      return { ok: false, error: e2 };
    }
  }
}

/**
 * Like `runAction`, for code that already has its own try/catch: on a
 * step-up refusal it asks and retries once; a cancel rethrows the refusal.
 */
export async function withStepUpRetry<T>(
  fn: () => Promise<T>,
  challenge?: Partial<StepUpChallenge>,
): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (!needsStepUp(e)) throw e;
    if (!(await askStepUp(challengeOf(e, challenge)))) throw e;
    return fn();
  }
}
